import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Card } from "../primitives";
import { DocuSignSettings } from "./DocuSignSettings";
import { SignNowSettings } from "./SignNowSettings";
import { SIGNNOW_ENABLED } from "../../constants";

// Wraps the existing DocuSign settings + the new SignNow settings under a
// single admin tab. Surfaces a "Default Provider" dropdown above the
// sub-tabs that writes app_settings.esign_default_provider — used by
// POST /api/esign/send when the modal doesn't pin a provider in the body.
//
// Scaffold note: the SignNow panel renders editable credential fields but
// the runtime surface is inert (signnow_enabled defaults to 'false', the
// Connect button is disabled). The follow-up session lights up SignNow
// end-to-end; the dropdown then becomes load-bearing for new envelopes.
export const ESignAdmin = () => {
  const [subTab, setSubTab] = useState<"docusign" | "signnow">("docusign");
  const [defaultProvider, setDefaultProvider] = useState<string>("docusign");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const loadDefault = async () => {
    const d = await apiCall("/esign/default-provider");
    if (d?.provider) setDefaultProvider(d.provider);
  };

  useEffect(() => { loadDefault(); }, []);

  const saveDefault = async (next: string) => {
    setSaving(true); setMsg("");
    setDefaultProvider(next);
    const r = await apiCall("/esign/config", {
      method: "PUT",
      body: JSON.stringify({ settings: { esign_default_provider: next } }),
    });
    setSaving(false);
    if (r?.success) {
      setMsg(`Default provider set to ${next}`);
      setTimeout(() => setMsg(""), 3000);
    } else if (r?.error) {
      setMsg(`Error: ${r.error}`);
    }
  };

  const tabBtn = (id: "docusign" | "signnow", label: string): React.CSSProperties => ({
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    border: subTab === id ? `1px solid ${T.acc}` : `1px solid ${T.brd}`,
    backgroundColor: subTab === id ? T.bg3 : "transparent",
    color: subTab === id ? T.tx : T.txM,
    cursor: "pointer",
    fontFamily: "inherit",
  });

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.txD, letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 4 }}>Default Provider</div>
            <select
              value={defaultProvider}
              onChange={e => { saveDefault(e.target.value); }}
              disabled={saving}
              style={{
                padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`,
                backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none",
                fontFamily: "inherit", minWidth: 180,
              }}
            >
              <option value="docusign">DocuSign</option>
              {SIGNNOW_ENABLED && <option value="signnow">SignNow</option>}
            </select>
          </div>
          <div style={{ fontSize: 11, color: T.txD, maxWidth: 480 }}>
            Used when sending an envelope without an explicit per-send override. Existing envelopes keep whichever provider they were created with.
          </div>
          {msg && <div style={{ fontSize: 11, color: msg.startsWith("Error") ? T.red : T.grn }}>{msg}</div>}
        </div>
      </Card>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={() => setSubTab("docusign")} style={tabBtn("docusign", "DocuSign")}>DocuSign</button>
        {SIGNNOW_ENABLED && <button onClick={() => setSubTab("signnow")} style={tabBtn("signnow", "SignNow")}>SignNow</button>}
      </div>

      {subTab === "docusign" && <DocuSignSettings />}
      {SIGNNOW_ENABLED && subTab === "signnow" && <SignNowSettings />}
    </div>
  );
};
